import React, {useState} from 'react';

import {useTranslation} from 'react-i18next';

import {ReactComponent as UploadIcon} from 'assets/svg/download.svg';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {ButtonVariant} from 'components/ui-kit/Button';
import {DropdownMenu} from 'components/ui-kit/DropdownMenu';
import {Icon} from 'components/ui-kit/Icon';
import {apiClient} from 'core/apiClient';
import {IS_DEMO, IS_NG} from 'core/constants/env';
import {showRestError} from 'core/errors';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {
    createProductFormDefaultsChanged,
    isFormModalOpenChanged,
    isModalOpenChanged,
} from 'models/registries/gtinsGrid/upload';
import {UserPermission, UserRoles} from 'types';

import {TableSearch} from '../Table/search';

import {ControlsWrapper, CreateButton, RecallButton} from './styled';

const getInternalProductCode = async () => {
    const {data} = await apiClient.get<string>('service/product-cards/internal-code');
    return data;
};

export const Controls = () => {
    const {t} = useTranslation();
    const [isInternalCodePending, setIsInternalCodePending] = useState(false);
    const IS_NG_DEMO = IS_NG && IS_DEMO;

    const openCreateProductForm = async (productType: 'GTIN' | 'INTERNAL') => {
        try {
            setIsInternalCodePending(productType === 'INTERNAL');
            const productCode =
                productType === 'INTERNAL' ? await getInternalProductCode() : undefined;

            createProductFormDefaultsChanged({
                product_type: productType,
                ...(productCode && {product_code: productCode}),
            });
            isFormModalOpenChanged(true);
        } catch (error) {
            showRestError(error);
            console.error(error);
        } finally {
            setIsInternalCodePending(false);
        }
    };

    return (
        <ControlsWrapper>
            <TableSearch />
            <PermissionAccess permissions={[UserPermission.GTIN_CREATE]}>
                <DropdownMenu
                    label={t('pages.registries.gtins.createProduct')}
                    buttonVariant={ButtonVariant.Normal}
                    buttonSize="sm"
                >
                    <DropdownMenu.List>
                        <DropdownMenu.Item onSelect={() => openCreateProductForm('GTIN')}>
                            {t('pages.registries.gtins.createGtinProduct')}
                        </DropdownMenu.Item>
                        <PermissionAccess permissions={[UserPermission.INTERNAL_CARD_CREATE]}>
                            <DropdownMenu.Item
                                disabled={isInternalCodePending}
                                onSelect={() => openCreateProductForm('INTERNAL')}
                            >
                                {t('pages.registries.gtins.createInternalProduct')}
                            </DropdownMenu.Item>
                        </PermissionAccess>
                    </DropdownMenu.List>
                </DropdownMenu>
            </PermissionAccess>
            {!IS_NG_DEMO && (
                <PermissionAccess permissions={[UserPermission.GTIN_CREATE]}>
                    <CreateButton
                        size="sm"
                        style={{whiteSpace: 'nowrap'}}
                        variant={ButtonVariant.Outline}
                        onClick={() => isModalOpenChanged(true)}
                    >
                        {t('pages.registries.gtins.uploadProduct')}
                        <Icon style={{marginLeft: 8}} source={UploadIcon} width={16} height={16} />
                    </CreateButton>
                </PermissionAccess>
            )}
        </ControlsWrapper>
    );
};
